import { captureException } from "@sentry/react";
import { backOff } from "exponential-backoff";
import { GraphQLClient } from "graphql-request";
import { createContext } from "preact";
import { uniqBy } from "lodash-es";
import type { ReactNode } from "preact/compat";
import { useContext, useEffect, useMemo, useRef, useState } from "preact/hooks";
import useLocalStorage from "@brickme/frontend-common/src/hooks/use-local-storage.ts";
import { createIpInfoGeolocationService } from "@brickme/frontend-common/src/ip-info/ip-info-service.ts";
import type { Region } from "~/utils/region.ts";
import type { ApiClient } from "~/api/client.ts";
import { createApiClient, requestBackOffOptions } from "~/api/client.ts";
import type { CountryCode } from "~/shopify-api/types.generated.ts";
import type {
	BricksProductsQuery,
	BricksProductsQueryVariables,
} from "./brick-products-query.shopify.generated.ts";
import bricksProductsQuery from "./brick-products-query.shopify.graphql";
import type {
	GetCountriesQuery,
	GetCountriesQueryVariables,
} from "./get-countries-query.shopify.generated.ts";
import getCountriesQuery from "./get-countries-query.shopify.graphql";

type RawBrickProduct = BricksProductsQuery["products"]["nodes"][number];

type BrickProduct = {
	readonly variants: RawBrickProduct["variants"]["nodes"];
};

type RegionCountry = {
	readonly regionId: string;
	readonly country: GetCountriesQuery["localization"]["availableCountries"][number];
};

type RegionServices = {
	readonly config: Region;
	readonly shopifyClient: GraphQLClient;
	readonly apiClient: ApiClient;
};

type RegionContextValue = {
	readonly countries: readonly RegionCountry[] | undefined;
	readonly selectedCountry: RegionCountry | undefined;
	readonly selectedRegion: RegionServices;
	readonly mainRegion: RegionServices;
	readonly brickProducts: readonly BrickProduct[] | undefined;
	readonly setColoursRequired: () => void;
	readonly setUserSelectedCountryCode: (newCountryCode: CountryCode) => void;
};

const context = createContext<RegionContextValue | undefined>(undefined);

async function fetchBrickProducts(
	shopifyClient: GraphQLClient,
	countryCode: CountryCode,
	signal: AbortSignal,
) {
	const { products } = await backOff(
		() =>
			shopifyClient.request<BricksProductsQuery, BricksProductsQueryVariables>({
				document: bricksProductsQuery,
				variables: {
					countryCode,
					query: "tag:pix-a-pic",
				},
				signal,
			}),
		{ ...requestBackOffOptions, retry: () => !signal.aborted },
	);
	return products.nodes.map((p) => ({
		variants: p.variants.nodes.map((v) => ({
			...v,
			id: v.id.replace("gid://shopify/ProductVariant/", ""),
		})),
	}));
}

const priority = ["AU", "NZ", "US"];

async function fetchCountries(shopifyClient: GraphQLClient) {
	const {
		localization: { availableCountries },
	} = await backOff(
		() =>
			shopifyClient.request<GetCountriesQuery, GetCountriesQueryVariables>(
				getCountriesQuery,
			),
		requestBackOffOptions,
	);
	return Array.from(availableCountries).sort((country1, country2) => {
		const priority1Index = priority.findIndex((p) => p === country1.isoCode);
		const priority2Index = priority.findIndex((p) => p === country2.isoCode);
		if (priority1Index === -1 && priority2Index === -1) {
			return country1.name.localeCompare(country2.name);
		}
		if (priority1Index === -1) {
			return 1;
		}
		if (priority2Index === -1) {
			return -1;
		}

		return priority1Index - priority2Index;
	});
}

type RegionProviderProps = {
	readonly regionConfigs: readonly Region[];
	readonly ipInfoToken: string;
	readonly children: ReactNode;
};

function RegionProvider({
	regionConfigs,
	ipInfoToken,
	children,
}: RegionProviderProps) {
	// Region specific stuff
	const regions = useMemo(() => {
		return regionConfigs.map(
			(config): RegionServices => ({
				config,
				shopifyClient: new GraphQLClient(
					`https://${config.shopifyShopDomain}/api/2025-01/graphql.json`,
					{
						headers: {
							"X-Shopify-Storefront-Access-Token": config.storefrontAccessToken,
						},
					},
				),
				apiClient: createApiClient(config.apiUrl),
			}),
		);
	}, [regionConfigs]);

	// User settings
	const [coloursRequired, setColoursRequired] = useState(false);
	const [userSelectedCountryCode, setUserSelectedCountryCode] =
		useState<CountryCode>();

	// All countries loaded
	const [countries, setCountries] = useState<readonly RegionCountry[]>();

	// Loading all countries
	useEffect(() => {
		(async () => {
			// allSettled: one region's storefront failing must not lose every other
			// region's countries, or the customer's own region can't price bricks.
			const results = await Promise.allSettled(
				regions.map(async (r) => {
					const countries = await fetchCountries(r.shopifyClient);
					return countries.map((country) => ({
						country,
						regionId: r.config.id,
					}));
				}),
			);
			const newRegionCountries = results.flatMap((result, i) => {
				if (result.status === "fulfilled") {
					return [result.value];
				}
				captureException(result.reason, {
					level: "warning",
					tags: { regionId: regions[i]?.config.id },
				});
				return [];
			});
			// Only take country of first region that allows it.
			const newCountries = uniqBy(
				newRegionCountries.flat(),
				(c) => c.country.isoCode,
			);
			setCountries(newCountries);
		})();
	}, [regions]);

	// Detect location
	const [detectedCountryCode, setDetectedCountryCode] = useLocalStorage({
		key: "ipCountry",
		defaultValue: undefined,
		parse: (s) => s,
		stringify: (s) => s,
	});
	const ipInfoTokenRef = useRef(ipInfoToken);
	ipInfoTokenRef.current = ipInfoToken;
	useEffect(() => {
		const abortController = new AbortController();
		(async () => {
			if (detectedCountryCode === undefined) {
				const result = await createIpInfoGeolocationService(
					ipInfoTokenRef.current,
				).getIpInfo({
					signal: abortController.signal,
				});
				// eslint-disable-next-line no-console
				console.log("Detected country code for ip", result);
				if (result !== undefined) {
					setDetectedCountryCode(result.country);
				}
			}
		})();
		return () => {
			abortController.abort();
		};
	}, [detectedCountryCode]);

	// Selected country + region
	const mainRegion = regions[0];
	const selectedCountry = (() => {
		// No countries loaded yet, select nothing
		if (countries === undefined) {
			return undefined;
		}

		// User has chosen a selected country, use that
		if (userSelectedCountryCode !== undefined) {
			const userSelectedCountry = countries.find(
				(c) => c.country.isoCode === userSelectedCountryCode,
			);
			if (userSelectedCountry === undefined) {
				throw new Error(
					`User selected country ${userSelectedCountry} doesn't exist`,
				);
			}
			return userSelectedCountry;
		}

		// Use the ip info detected country code
		if (detectedCountryCode !== undefined) {
			const detectedCountry = countries.find(
				(c) => c.country.isoCode === detectedCountryCode,
			);
			// Might be undefined if don't support detected country, that's okay.
			if (detectedCountry !== undefined) {
				return detectedCountry;
			}
		}

		// Use the first country
		return countries[0];
	})();
	const selectedRegion =
		regions.find((r) => r.config.id === selectedCountry?.regionId) ??
		mainRegion;

	// Load brick products
	const [brickProducts, setBrickProducts] = useState<readonly BrickProduct[]>();
	useEffect(() => {
		if (!coloursRequired || selectedCountry === undefined) {
			return;
		}

		const abortController = new AbortController();
		(async () => {
			setBrickProducts(undefined);
			let newProducts: Awaited<ReturnType<typeof fetchBrickProducts>>;
			try {
				newProducts = await fetchBrickProducts(
					selectedRegion.shopifyClient,
					selectedCountry.country.isoCode,
					abortController.signal,
				);
			} catch (e) {
				// Abort expected
				if (abortController.signal.aborted) {
					return;
				}
				throw e;
			}

			if (newProducts.length === 0) {
				throw new Error("No brick products found");
			}
			setBrickProducts(newProducts);
		})();
		return () => {
			abortController.abort("Brick products aborted");
		};
	}, [coloursRequired, selectedRegion, selectedCountry]);

	// Context value
	const value = useMemo(
		() => ({
			brickProducts,
			countries,
			setColoursRequired: () => setColoursRequired(true),
			selectedRegion,
			mainRegion,
			selectedCountry,
			setUserSelectedCountryCode,
		}),
		[brickProducts, mainRegion, selectedRegion, selectedCountry, countries],
	);

	return <context.Provider value={value}>{children}</context.Provider>;
}

function useRegion() {
	const value = useContext(context);
	if (value === undefined) {
		throw new Error("No region context");
	}
	return value;
}

function useSelectedRegion() {
	return useRegion().selectedRegion;
}

function useAvailableCountries() {
	return useRegion().countries;
}

function useShopifyBrickProducts() {
	return useRegion().brickProducts;
}

function useSetColoursRequired() {
	return useRegion().setColoursRequired;
}

function useSelectedCountryCode() {
	const { setUserSelectedCountryCode, selectedCountry } = useRegion();
	return {
		selectedCountry,
		setUserSelectedCountryCode,
	};
}

function useSelectedRegionApiClient() {
	return useSelectedRegion().apiClient;
}

function useMainRegionApiClient() {
	return useRegion().mainRegion.apiClient;
}

function useStoreAddToCartUrl() {
	return useSelectedRegion().config.storeAddToCartUrl;
}

export type { BrickProduct, RegionProviderProps };
export {
	useStoreAddToCartUrl,
	useMainRegionApiClient,
	useSelectedRegionApiClient,
	useAvailableCountries,
	useSelectedCountryCode,
	useSetColoursRequired,
	useShopifyBrickProducts,
};
export { RegionProvider };
